import {
  RuleDeletionAlert,
  RulesContent,
  type RulesContentListProps,
  RulesManagerEmptySearch,
  RulesManagerFilters,
  useRulesManagerFilters,
} from '@carboncopyco/editor-ui';
import {
  type FindReplaceRule,
  IFindAndReplaceRuleType,
  type Modification,
} from '@carboncopyco/types';
import { type DropdownFilterItemContent, notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useEditor } from '../../../context/EditorSdkProvider';
import { orderRulesWithGroups } from '../../utils';
import { FindAndReplaceManagerContext } from '../FindAndReplaceManagerContext';
import { FindAndReplaceRulePreview } from '../FindAndReplaceRulePreview';
import { FindAndReplaceRuleCreation } from './FindAndReplaceRuleCreation';
import {
  FindAndReplaceRuleListItem,
  FindAndReplaceRuleListItemGroup,
} from './FindAndReplaceRuleListItem';

export interface FindAndReplaceRulesContentProps {
  ruleToCreate: IFindAndReplaceRuleType;
  cancelCreation: () => void;
}

export type Filters = 'all' | 'text' | 'image' | 'links';

const ruleFilterToTypes: Record<Filters, IFindAndReplaceRuleType[]> = {
  all: [
    IFindAndReplaceRuleType.replaceText,
    IFindAndReplaceRuleType.replaceImage,
    IFindAndReplaceRuleType.replaceImageWithVariable,
    IFindAndReplaceRuleType.replaceLink,
  ],
  text: [IFindAndReplaceRuleType.replaceText],
  image: [
    IFindAndReplaceRuleType.replaceImage,
    IFindAndReplaceRuleType.replaceImageWithVariable,
  ],
  links: [IFindAndReplaceRuleType.replaceLink],
};

export const FindAndReplaceRulesContent = ({
  ruleToCreate,
  cancelCreation,
}: FindAndReplaceRulesContentProps) => {
  const { rules, createRule, editRule, deleteRule, reorderRules } = useContext(
    FindAndReplaceManagerContext
  );

  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [selectedRule, setSelectedRule] = useState<
    FindReplaceRule & {
      isLastRuleInGroup?: boolean;
      groupId?: string;
    }
  >();
  const editorSdk = useEditor();

  const filterByRuleType = useCallback(
    (rule: FindReplaceRule, filter: Filters) =>
      ruleFilterToTypes[filter ?? 'all'].includes(rule.type),
    []
  );

  const filterOptions = useMemo(
    () => ({
      initialFilter: 'all' as Filters,
      searchKeys: [
        'newValue',
        'oldValue',
        'sourceFileMetadata.name',
        'destinationFileMetadata.name',
      ],
      dataSet: rules,
      filterFunc: filterByRuleType,
    }),
    [rules, filterByRuleType]
  );

  const {
    setSearchValue,
    searchValue,
    filter,
    setFilter,
    filteredList: filteredRules,
    isFiltersActive,
  } = useRulesManagerFilters<Filters, FindReplaceRule>(filterOptions);

  const rulesWithoutSubGroups = useMemo(
    () =>
      orderRulesWithGroups(
        filteredRules as unknown as Modification[],
        editorSdk.getGroupData
      ),
    [filteredRules]
  );

  const onReorderRules: RulesContentListProps<FindReplaceRule>['onOrderChange'] =
    async ({ rules, destinationIndex }) => {
      const moved = rules[destinationIndex].rule;
      const after = rules[destinationIndex + 1]?.rule;
      await reorderRules(moved, after).catch(() => {
        notify.snackbar({
          type: 'error',
          label: 'Failed to reorder. Please try again',
        });
      });
    };

  const items = useMemo(
    () => [
      { id: 'all', label: 'Any type' },
      { id: 'image', label: 'Image' },
      { id: 'text', label: 'Text' },
      { id: 'links', label: 'Link' },
    ],
    []
  );

  return (
    <>
      <RulesContent>
        {!!rules?.length && (
          <RulesManagerFilters<Filters>
            onSearchChange={setSearchValue}
            onFiltersChange={setFilter}
            dropdownItems={items as DropdownFilterItemContent<Filters>[]}
            searchValue={searchValue}
            selectedFilter={filter}
          />
        )}
        <RulesContent.CreationForm show={!!ruleToCreate}>
          <FindAndReplaceRuleCreation
            rules={rules}
            ruleType={ruleToCreate}
            onCreate={createRule}
            onCancel={cancelCreation}
          />
        </RulesContent.CreationForm>
        {rulesWithoutSubGroups.length ? (
          <RulesContent.List
            draggable={!isFiltersActive}
            rules={rulesWithoutSubGroups.map((rule) => ({
              id: rule.ruleId ?? rule.content.id,
              rule,
            }))}
            onOpenRule={cancelCreation}
            onOrderChange={onReorderRules}
            renderItem={({ ruleItem, open, openRule, closeRule }) =>
              ruleItem.rule.hasOwnProperty('children') ? (
                <FindAndReplaceRuleListItemGroup
                  open={open}
                  rule={ruleItem.rule}
                  onEdit={editRule}
                  onDelete={(rule) => {
                    setSelectedRule({
                      ...rule,
                      isLastRuleInGroup: ruleItem.rule['children'].length === 1,
                    });
                    setShowDeletionAlert.setTrue();
                  }}
                />
              ) : (
                <FindAndReplaceRuleListItem
                  rule={ruleItem.rule}
                  rules={rulesWithoutSubGroups}
                  onEdit={editRule}
                  onDelete={(rule) => {
                    setSelectedRule(rule);
                    setShowDeletionAlert.setTrue();
                  }}
                  open={open}
                  onOpenRule={openRule}
                  onCloseRule={closeRule}
                />
              )
            }
          />
        ) : !ruleToCreate ? (
          <RulesManagerEmptySearch />
        ) : null}
      </RulesContent>
      <RuleDeletionAlert
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onConfirm={async () => {
          await deleteRule(selectedRule);
          if (selectedRule?.isLastRuleInGroup && selectedRule?.groupId) {
            await editorSdk.deleteAiPromptModification(
              editorSdk.getGroupData(selectedRule.groupId)
            );
          }
          setShowDeletionAlert.setFalse();
        }}
        textLabel={
          selectedRule?.isLastRuleInGroup
            ? 'Deleting this group will also remove the associated rule created by the AI \n' +
              'Wizard tool. Are you sure you want to delete?'
            : undefined
        }
        rulePreview={<FindAndReplaceRulePreview rule={selectedRule} />}
      />
    </>
  );
};
